import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fmtPct, appliquerDecoupage } from './calcul';

function fmt(n: number): string {
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}
import { Session, Decoupage } from '../types';

type Color = [number, number, number];

const BF:  Color = [26,  53,  88];
const BM:  Color = [44,  95,  158];
const BC:  Color = [214, 228, 247];
const GL:  Color = [244, 246, 249];
const GM:  Color = [203, 213, 225];
const VT:  Color = [22,  163, 74];
const VTL: Color = [220, 252, 231];
const OB:  Color = [255, 247, 237];
const AM:  Color = [180, 100, 10];
const TX:  Color = [30,  41,  59];
const TS:  Color = [100, 116, 139];
const W:   Color = [255, 255, 255];
const DC:  Color = [5,   150, 105];
const DCL: Color = [209, 250, 229];
const DCB: Color = [167, 243, 208];

const ML = 14;
const MR = 14;

const TYPE_LABELS: Record<string, string> = { depot: 'Dépôt', retrait: 'Retrait', tous: 'Tous' };

function fmtH(h: number): string {
  return `${String(h).padStart(2, '0')}h`;
}
function fmtTime(h: number, m: number): string {
  return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}`;
}

function drawBullet(doc: jsPDF, cx: number, cy: number, r: number, color: Color): void {
  doc.setFillColor(...color);
  doc.circle(cx, cy, r, 'F');
}

function drawCheck(doc: jsPDF, x: number, y: number, sz: number, color: Color): void {
  doc.setDrawColor(...color);
  doc.setLineWidth(sz * 0.18);
  doc.line(x,             y + sz * 0.55, x + sz * 0.35, y + sz * 0.9);
  doc.line(x + sz * 0.35, y + sz * 0.9,  x + sz,        y);
  doc.setLineWidth(0.3);
}

function drawHeader(doc: jsPDF, titre: string, sousTitre: string): void {
  const pw = doc.internal.pageSize.getWidth();
  doc.setFillColor(...BF);
  doc.rect(0, 0, pw, 38, 'F');
  doc.setFillColor(...BM);
  doc.rect(0, 34, pw, 4, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(...W);
  doc.text(titre, pw / 2, 16, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...BC);
  doc.text(sousTitre, pw / 2, 27, { align: 'center' });
  doc.setTextColor(...TX);
}

function drawSectionTitle(doc: jsPDF, num: number | string, titre: string, y: number): number {
  const pw = doc.internal.pageSize.getWidth();
  const w  = pw - ML - MR;
  doc.setFillColor(...BF);
  doc.rect(ML, y, w, 9, 'F');
  doc.setFillColor(...BM);
  doc.rect(ML, y, 11, 9, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...W);
  doc.text(String(num), ML + 5.5, y + 6.2, { align: 'center' });
  doc.text(titre, ML + 14, y + 6.2);
  doc.setTextColor(...TX);
  return y + 14;
}

function drawDecoupageTitle(doc: jsPDF, nom: string, y: number): number {
  const pw = doc.internal.pageSize.getWidth();
  const w  = pw - ML - MR;
  doc.setFillColor(...DCL);
  doc.roundedRect(ML, y, w, 9, 2, 2, 'F');
  doc.setDrawColor(...DCB);
  doc.setLineWidth(0.4);
  doc.roundedRect(ML, y, w, 9, 2, 2, 'S');
  doc.setFillColor(...DC);
  doc.rect(ML, y, 4, 9, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...DC);
  doc.text(nom, ML + 9, y + 6);
  doc.setTextColor(...TX);
  return y + 13;
}

function drawBox(doc: jsPDF, text: string, y: number, bg: Color = BC, border: Color = BM): number {
  const pw = doc.internal.pageSize.getWidth();
  const w  = pw - ML - MR;
  doc.setFillColor(...bg);
  doc.roundedRect(ML, y, w, 13, 2, 2, 'F');
  doc.setDrawColor(...border);
  doc.setLineWidth(0.5);
  doc.roundedRect(ML, y, w, 13, 2, 2, 'S');
  doc.setFillColor(...border);
  doc.rect(ML, y, 3, 13, 'F');
  doc.setFont('courier', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...BF);
  doc.text(text, pw / 2, y + 8.7, { align: 'center' });
  doc.setTextColor(...TX);
  return y + 17;
}

interface InfoCard { label: string; value: string; ok?: boolean; }

function drawInfoCards(doc: jsPDF, cards: InfoCard[], y: number, compact = false): number {
  const pw    = doc.internal.pageSize.getWidth();
  const w     = pw - ML - MR;
  const cardW = w / cards.length;
  const h     = compact ? 13 : 19;

  doc.setFillColor(...GL);
  doc.roundedRect(ML, y, w, h, 2, 2, 'F');
  doc.setDrawColor(...GM);
  doc.setLineWidth(0.3);
  doc.roundedRect(ML, y, w, h, 2, 2, 'S');

  cards.forEach((card, i) => {
    const x = ML + i * cardW;
    if (i > 0) {
      doc.setDrawColor(...GM);
      doc.setLineWidth(0.3);
      doc.line(x, y + 2, x, y + h - 2);
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(compact ? 6 : 7);
    doc.setTextColor(...TS);
    doc.text(card.label, x + cardW / 2, y + (compact ? 5 : 7.5), { align: 'center' });
    const valueColor = card.ok ? VT : BF;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(compact ? 8 : 9.5);
    doc.setTextColor(...valueColor);
    doc.text(card.value, x + cardW / 2, y + (compact ? 10.5 : 15), { align: 'center' });
  });

  cards.forEach((card, i) => {
    if (card.ok) {
      const x = ML + i * cardW;
      drawCheck(doc, x + cardW - 7, y + 2, compact ? 3 : 4, VT);
    }
  });

  doc.setTextColor(...TX);
  return y + h + 4;
}

function drawFooter(doc: jsPDF, pageNum: number, totalPages: number, base: number | null, controleur: string): void {
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...GM);
  doc.setLineWidth(0.3);
  doc.line(ML, ph - 13, pw - MR, ph - 13);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...TS);
  const baseLabel = base ? `Multiple de ${base}` : 'Arrondi au multiple de 50';
  doc.text(`Répartition en parts inégales — ${baseLabel} — Total garanti exact`, ML, ph - 8);
  if (controleur) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BF);
    doc.text(`Contrôleur : ${controleur}`, pw / 2, ph - 8, { align: 'center' });
  }
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...TS);
  doc.text(`Page ${pageNum} / ${totalPages}`, pw - MR, ph - 8, { align: 'right' });
}

function buildDoc(sessions: Session[], decoupages: Decoupage[]): jsPDF {
  const doc   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw    = doc.internal.pageSize.getWidth();
  const ph    = doc.internal.pageSize.getHeight();
  const avail = pw - ML - MR;
  const BOTTOM = ph - 14;

  const now     = new Date();
  const genDate = now.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const genTime = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const genLabel = `Généré le ${genDate} à ${genTime}`;

  sessions.forEach((session, si) => {
    if (si > 0) doc.addPage();

    // ── En-tête compact (20mm) ──
    doc.setFillColor(...BF);
    doc.rect(0, 0, pw, 20, 'F');
    doc.setFillColor(...BM);
    doc.rect(0, 17, pw, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...W);
    doc.text(
      `Répartition N° ${si + 1}${session.zone ? ` — ${session.zone}` : ''}`,
      pw / 2, 9, { align: 'center' }
    );
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...BC);
    doc.text(
      `${fmt(session.montant)} FCFA  ·  ${session.parts.length} parts  ·  Arrondi au multiple de ${session.base}  ·  Total garanti exact`,
      pw / 2, 15.5, { align: 'center' }
    );
    // Horodatage génération — coin supérieur droit
    doc.setFontSize(6.5);
    doc.text(genLabel, pw - MR, 5, { align: 'right' });
    doc.setTextColor(...TX);

    let sy = 24;

    const totalVerif = session.resultats.reduce((s, r) => s + r.part, 0);
    const nbAjust    = session.resultats.filter(r => r.ajuste).length;
    const exact      = totalVerif === session.montant;

    // ── Cartes info compactes ──
    const infoCards: InfoCard[] = [];
    if (session.zone) infoCards.push({ label: 'ZONE', value: session.zone });
    infoCards.push(
      { label: 'MONTANT TOTAL',   value: fmt(session.montant) + ' FCFA' },
      { label: 'NOMBRE DE PARTS', value: String(session.parts.length) },
      { label: 'ARRONDI',         value: `Multiple de ${session.base}` },
      { label: 'TOTAL VÉRIFIÉ',   value: fmt(totalVerif) + ' FCFA', ok: exact },
    );
    sy = drawInfoCards(doc, infoCards, sy, true);

    // ── Tableau principal ──
    const totalPoids = session.parts.reduce((s, p) => s + p.poids, 0);
    const rows: string[][] = session.resultats.map(r => [
      r.nom || `Personne ${r.index + 1}`,
      `${fmtTime(r.heureDebut, r.minuteDebut)}-${fmtTime(r.heureFin, r.minuteFin)}`,
      String(r.poids),
      fmt(r.part) + (r.ajuste ? ' *' : ''),
      fmtPct(r.pourcentage),
    ]);
    rows.push(['TOTAL', '', String(totalPoids), fmt(totalVerif), '100,00 %']);

    autoTable(doc, {
      startY: sy,
      head: [['Bénéficiaire', 'Plage horaire', 'Poids', 'Montant arrondi', 'Part %']],
      body: rows,
      theme: 'plain',
      styles: { fontSize: 7.5, font: 'helvetica', cellPadding: { top: 2, right: 3, bottom: 2, left: 3 }, textColor: TX },
      headStyles: { fillColor: BM, textColor: W, fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: GL },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 32, font: 'courier', fontSize: 7 },
        2: { cellWidth: 16, halign: 'right' },
        3: { cellWidth: 58, halign: 'right' },
        4: { cellWidth: 26, halign: 'right' },
      },
      willDrawCell(data) {
        if (data.section === 'body') {
          const ri = data.row.index;
          if (ri === rows.length - 1) doc.setFillColor(...BC);
          else if (session.resultats[ri]?.ajuste) doc.setFillColor(...OB);
        }
      },
      margin: { left: ML, right: MR },
    });

    sy = (doc as any).lastAutoTable.finalY + 4;

    // ── Découpages horaires (si la place le permet) ──
    if (decoupages.length > 0 && sy + 25 < BOTTOM) {
      sy += 3;
      sy = drawSectionTitle(doc, '⏱', `Découpages horaires (${decoupages.length} modèle${decoupages.length > 1 ? 's' : ''})`, sy);

      decoupages.forEach((decoupage, di) => {
        if (sy + 20 >= BOTTOM) return;
        const resultatDecoupe = appliquerDecoupage(session.resultats, decoupage, session.base);
        const nTranches = decoupage.tranches.length;
        sy = drawDecoupageTitle(doc, `${di + 1}. ${decoupage.nom}`, sy);

        const benW    = 34;
        const plageW  = 22;
        const totW    = 20;
        const remainW = avail - benW - plageW - totW;
        const trW     = Math.max(12, Math.floor(remainW / nTranches));

        const matHead = [
          'Bénéficiaire', 'Plage',
          ...decoupage.tranches.map(t =>
            `${fmtH(t.heureDebut)}-${fmtH(t.heureFin)}\n${TYPE_LABELS[t.typeTransaction] ?? t.typeTransaction}\n${t.pourcentage}%`
          ),
          'Total',
        ];
        const matBody: string[][] = resultatDecoupe.map(r => [
          r.nom || `Personne ${r.index + 1}`,
          `${fmtTime(r.heureDebut, r.minuteDebut)}-${fmtTime(r.heureFin, r.minuteFin)}`,
          ...r.tranchesDecoupage.map(t => fmt(t.montant)),
          fmt(r.part),
        ]);
        matBody.push([
          'TOTAL', '',
          ...decoupage.tranches.map((_, j) =>
            fmt(resultatDecoupe.reduce((s, r) => s + r.tranchesDecoupage[j].montant, 0))
          ),
          fmt(totalVerif),
        ]);

        const colStyles: Record<number, object> = {
          0: { cellWidth: benW },
          1: { cellWidth: plageW, font: 'courier', fontSize: 6.5 },
          [nTranches + 2]: { cellWidth: totW, halign: 'right', fontStyle: 'bold', textColor: BF },
        };
        for (let j = 0; j < nTranches; j++) colStyles[j + 2] = { cellWidth: trW, halign: 'right' };

        autoTable(doc, {
          startY: sy,
          head: [matHead],
          body: matBody,
          theme: 'plain',
          styles: { fontSize: 7, font: 'helvetica', cellPadding: { top: 2, right: 2, bottom: 2, left: 2 }, textColor: TX, overflow: 'linebreak' },
          headStyles: { fillColor: DC, textColor: W, fontStyle: 'bold', fontSize: 6.5, minCellHeight: 12, valign: 'middle' },
          alternateRowStyles: { fillColor: [240, 253, 244] as Color },
          columnStyles: colStyles,
          willDrawCell(data) {
            if (data.section === 'body' && data.row.index === matBody.length - 1) doc.setFillColor(...DCL);
          },
          margin: { left: ML, right: MR },
        });

        sy = (doc as any).lastAutoTable.finalY + 3;
      });
    }
  });

  // ── Footer sur chaque page ──
  const totalPages = (doc.internal as any).getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const s = sessions[i - 1];
    drawFooter(doc, i, totalPages, s?.base ?? null, s?.controleur ?? '');
  }

  return doc;
}

export function exportPDF(sessions: Session[], decoupages: Decoupage[]): void {
  buildDoc(sessions, decoupages).save('repartition_parts_inegales.pdf');
}

export function getPDFBlob(sessions: Session[], decoupages: Decoupage[]): Blob {
  return buildDoc(sessions, decoupages).output('blob') as Blob;
}
